import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import {
  QuickAction,
  QUICK_ACTIONS_STORAGE_KEY,
  DEFAULT_QUICK_ACTION_IDS,
  MAX_QUICK_ACTIONS,
  getQuickActionById,
  getPermanentActionIds,
  createSeparator,
  isSeparatorId,
} from "../config/QuickActionConfig";

interface QuickActionsContextType {
  selectedActionIds: string[];
  selectedActions: QuickAction[];
  addAction: (actionId: string) => boolean;
  addSeparator: () => boolean;
  removeAction: (actionId: string) => void;
  reorderActions: (actionIds: string[]) => void;
  isActionSelected: (actionId: string) => boolean;
  isActionPermanent: (actionId: string) => boolean;
  canAddMore: boolean;
  maxActions: number;
}

const permanentIds = getPermanentActionIds();

const defaultContextValue: QuickActionsContextType = {
  selectedActionIds: DEFAULT_QUICK_ACTION_IDS,
  selectedActions: [],
  addAction: () => false,
  addSeparator: () => false,
  removeAction: () => {},
  reorderActions: () => {},
  isActionSelected: () => false,
  isActionPermanent: () => false,
  canAddMore: true,
  maxActions: MAX_QUICK_ACTIONS,
};

export const QuickActionsContext = createContext<QuickActionsContextType>(defaultContextValue);

interface QuickActionsProviderProps {
  children: ReactNode;
}

// Ensures permanent actions are always first and included
const ensurePermanentActions = (actionIds: string[]): string[] => {
  // Remove permanent IDs from the list (we'll add them at the front)
  const nonPermanentIds = actionIds.filter((id) => !permanentIds.includes(id));
  // Return permanent IDs first, then the rest
  return [...permanentIds, ...nonPermanentIds];
};

export const QuickActionsProvider: React.FC<QuickActionsProviderProps> = ({ children }) => {
  const [selectedActionIds, setSelectedActionIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(QUICK_ACTIONS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return ensurePermanentActions(parsed);
        }
      }
    } catch (error) {
      console.error("Error reading quick actions from localStorage:", error);
    }
    return ensurePermanentActions(DEFAULT_QUICK_ACTION_IDS);
  });

  // Persist to localStorage whenever selectedActionIds changes
  useEffect(() => {
    try {
      localStorage.setItem(
        QUICK_ACTIONS_STORAGE_KEY,
        JSON.stringify(selectedActionIds)
      );
    } catch (error) {
      console.error("Error saving quick actions to localStorage:", error);
    }
  }, [selectedActionIds]);

  // Get the full QuickAction objects for selected IDs
  // Handle both regular actions and separators
  const selectedActions: QuickAction[] = selectedActionIds
    .map((id) => {
      if (isSeparatorId(id)) {
        // Create a separator object for separator IDs
        return {
          id,
          title: "Separator",
          description: "Visual separator between buttons",
          icon: "minus",
          to: "",
          category: "Utility",
          isSeparator: true,
        } as QuickAction;
      }
      return getQuickActionById(id);
    })
    .filter((action): action is QuickAction => action !== undefined);

  // Add an action to the panel
  const addAction = useCallback(
    (actionId: string): boolean => {
      if (selectedActionIds.length >= MAX_QUICK_ACTIONS) {
        return false;
      }
      if (selectedActionIds.includes(actionId)) {
        return false;
      }
      setSelectedActionIds((prev) => ensurePermanentActions([...prev, actionId]));
      return true;
    },
    [selectedActionIds]
  );

  // Add a separator to the panel
  const addSeparator = useCallback((): boolean => {
    if (selectedActionIds.length >= MAX_QUICK_ACTIONS) {
      return false;
    }
    const separator = createSeparator();
    setSelectedActionIds((prev) => ensurePermanentActions([...prev, separator.id]));
    return true;
  }, [selectedActionIds]);

  // Remove an action from the panel (cannot remove permanent actions)
  const removeAction = useCallback((actionId: string): void => {
    // Prevent removal of permanent actions
    if (permanentIds.includes(actionId)) {
      return;
    }
    setSelectedActionIds((prev) => prev.filter((id) => id !== actionId));
  }, []);

  // Reorder actions (permanent actions stay at the front)
  const reorderActions = useCallback((actionIds: string[]): void => {
    setSelectedActionIds(ensurePermanentActions(actionIds));
  }, []);

  // Check if an action is selected
  const isActionSelected = useCallback(
    (actionId: string): boolean => {
      return selectedActionIds.includes(actionId);
    },
    [selectedActionIds]
  );

  // Check if an action is permanent (cannot be removed)
  const isActionPermanent = useCallback((actionId: string): boolean => {
    return permanentIds.includes(actionId);
  }, []);

  const value: QuickActionsContextType = {
    selectedActionIds,
    selectedActions,
    addAction,
    addSeparator,
    removeAction,
    reorderActions,
    isActionSelected,
    isActionPermanent,
    canAddMore: selectedActionIds.length < MAX_QUICK_ACTIONS,
    maxActions: MAX_QUICK_ACTIONS,
  };

  return (
    <QuickActionsContext.Provider value={value}>
      {children}
    </QuickActionsContext.Provider>
  );
};
